import i18n from '@/i18n';
import { useCompanyStore } from '@/stores/company';
import { useThemeStore } from '@/stores/theme';

export const lightColor = (color: string): string => {
  if (!/^#(?:[0-9a-f]{3}){1,2}$/i.test(color)) {
    return '';
  }

  const { theme } = useThemeStore();
  const { companySettings } = useCompanyStore();
  const darkerColors = theme === 'DARK' || companySettings.agenda?.darkerColors;

  // normalize to 6-char hex
  let hex = color.replace(/^#/, '');
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);

  // lighten towards white by a factor
  // e.g. 0.3 → 30% lighter, 0.5 → 50% lighter
  const factor = darkerColors ? 0.5 : 0.7;

  const lighten = (channel: number) =>
    Math.round(channel + (255 - channel) * factor);

  const rr = lighten(r).toString(16).padStart(2, '0');
  const gg = lighten(g).toString(16).padStart(2, '0');
  const bb = lighten(b).toString(16).padStart(2, '0');

  return `#${rr}${gg}${bb}`;
};

export const underscore = (value: string): string =>
  value.replace(
    /[A-Z]/g,
    (char, index) => (index !== 0 ? '_' : '') + char.toLowerCase(),
  );

export function underscoreObject(value: any) {
  const newObject: any = {};
  Object.keys(value).forEach((key) => {
    if (Array.isArray(value[key])) {
      newObject[underscore(key)] = value[key].map(underscoreObject);
    } else {
      newObject[underscore(key)] = value[key];
    }
  });
  return newObject;
}

export const uniqueArray = (arr: any[], key = 'id') => [
  ...new Map(arr.map((item) => [item[key], item])).values(),
];

export const uniqueDiscountCodes = (appointments: any[]) =>
  uniqueArray(
    appointments
      .map(
        (appointment) => appointment.discount_code || appointment.discountCode,
      )
      .filter((discount) => discount?.code),
  ).map((discount) => ({
    code: discount.code,
    discountType: discount.discount_type || discount.discountType,
    discountPercentage:
      discount.discount_percentage || discount.discountPercentage,
    discountAmount: discount.discount_amount || discount.discountAmount,
    serviceIds: discount.service_ids || discount.serviceIds,
  }));

export const groupBy = (data: any[], key: string) => {
  const groupedData: any[] = [];
  data.forEach((item) => {
    const group = groupedData.find((g) => g.name === item[key]);
    if (group) {
      group.items.push(item);
    } else {
      groupedData.push({
        name: item[key],
        items: [item],
      });
    }
  });
  return groupedData;
};

export const formatFilters = (filters: any[]) => {
  let labelIndex = 0;
  // fun mix of graphql and rest
  const mapped = filters.map((filter) => {
    const newObj = { ...filter };
    newObj.labelIndex = labelIndex;
    labelIndex++;
    if (newObj.filterName) {
      newObj.filter_name = newObj.filterName;
      newObj.filterName = undefined;
    }

    return newObj;
  });
  return mapped;
};

export const addLabelIndex = (filters: any[], filter: any) => {
  // labels need an index so that they can be recognised when a user removes one
  if (!filters.length) {
    filter.labelIndex = 0;
  } else if (filter) {
    filter.labelIndex = filters[filters.length - 1].labelIndex + 1;
  }
  return filter;
};

export const resourceIcon = (resourceType: string) => {
  switch (resourceType) {
    case 'employee':
      return 'person';
    case 'room':
      return 'pin';
    case 'equipment':
      return 'monitor';
  }
};

export const formatEnters = (value: string): string => {
  if (!value) {
    return '';
  }

  return value.replace(/(\r\n|\n|\r)/gm, '<br>');
};

export const giftCardCode = (value: string): string =>
  value.replace(/\d{4}(?=.)/g, '$& ');

export const twoDecimals = (value: number): number =>
  Math.round(value * 100) / 100;

export const capitalize = (value: string): string => {
  if (!value) {
    return '';
  }

  return typeof value === 'string'
    ? value.charAt(0).toUpperCase() + value.slice(1)
    : value;
};

export const replacePinapparaat = (message: string, country: string) => {
  let newText = message;

  // if company is belgium, replace pinapparaat with betaalterminal
  if (country === 'be') {
    newText = newText.replaceAll('pinapparaat', 'betaalterminal');
    newText = newText.replaceAll('pinapparaten', 'betaalterminals');
  }

  return newText;
};

export const underscoreToDash = (value: string): string =>
  value.replace(/_/g, '-');

export const dashToUnderscore = (value: string): string =>
  value.replace(/-/g, '_');

export const localeString = (value: number) =>
  value.toLocaleString(i18n.locale.value);

export const stripHtml = (html: string): string => {
  if (typeof html !== 'string') return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent?.trim() ?? '';
};

// CHE-XXX.XXX.XXX
export const formatSwissRegistrationNumber = (raw: string): string => {
  const digits = raw.toUpperCase().replace(/\D/g, '').slice(0, 9);
  if (!digits) return '';
  let formatted = `CHE-${digits.slice(0, 3)}`;
  if (digits.length > 3) formatted += `.${digits.slice(3, 6)}`;
  if (digits.length > 6) formatted += `.${digits.slice(6, 9)}`;
  return formatted;
};

export const plaintextToHtml = (text: string): string => {
  if (typeof text !== 'string') return '';
  if (/^<[a-z]/i.test(text)) return text; // First check if text is already HTML

  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/(\r\n|\n|\r)/gm, '<br>');
};
